import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { Card, type CardSurface, type CardTone } from '@/components/ui/card'
import { cn } from '@/lib/utils'

interface TableSlot {
  element: HTMLElement | null
  setElement: (element: HTMLElement | null) => void
}

interface TableSlots {
  /** At the very start of the header, before the list name or tabs — where
   *  the bulk actions for selected rows go. */
  bulk: TableSlot
  /** Beside the list name — where the column picker goes. */
  columns: TableSlot
  /** At the far right of the header, after the page-size picker and the
   *  page's own buttons — where the table's edit toggle goes. */
  actions: TableSlot
}

const TableSlotsContext = createContext<TableSlots | null>(null)

/**
 * Card holding a list. It offers two slots in its header row for the controls
 * of the table inside it, so they sit in the header beside the list name and
 * its actions instead of in a strip of their own.
 */
export function ListCard({
  children,
  className,
  tone,
  surface,
}: {
  children: ReactNode
  className?: string
  /** What the list holds; colours the icon beside its name. */
  tone?: CardTone
  /** `sunken` for a read-only card beside a form; see {@link CardSurface}. */
  surface?: CardSurface
}) {
  const [bulk, setBulk] = useState<HTMLElement | null>(null)
  const [columns, setColumns] = useState<HTMLElement | null>(null)
  const [actions, setActions] = useState<HTMLElement | null>(null)

  const slots = useMemo<TableSlots>(
    () => ({
      bulk: { element: bulk, setElement: setBulk },
      columns: { element: columns, setElement: setColumns },
      actions: { element: actions, setElement: setActions },
    }),
    [bulk, columns, actions],
  )

  return (
    <TableSlotsContext.Provider value={slots}>
      <Card tone={tone} surface={surface} className={cn('overflow-hidden', className)}>
        {children}
      </Card>
    </TableSlotsContext.Provider>
  )
}

/**
 * Marks where the bulk actions for selected rows go. `ListCardHeader` renders
 * one already; place it first in a header the page builds itself. It takes no
 * room until a selection fills it.
 */
export function BulkActionsSlot() {
  const slots = useContext(TableSlotsContext)
  if (!slots) return null
  return <div ref={slots.bulk.setElement} className="flex items-center empty:hidden" />
}

/**
 * Marks where the table's column picker goes. `ListCardHeader` renders one
 * already; place it by hand in a header the page builds itself. It takes no
 * room until the table fills it.
 */
export function ColumnsSlot() {
  const slots = useContext(TableSlotsContext)
  if (!slots) return null
  return <div ref={slots.columns.setElement} className="flex items-center gap-2 empty:hidden" />
}

/** Marks where the table's own actions go — at the end of the header row. */
export function TableActionsSlot() {
  const slots = useContext(TableSlotsContext)
  if (!slots) return null
  return <div ref={slots.actions.setElement} className="flex items-center gap-2 empty:hidden" />
}

/** The slot `BulkActionBar` should render into, if any. */
export function useBulkActionsSlot() {
  return useContext(TableSlotsContext)?.bulk ?? null
}

/** The slot a `DataTable` should render its column picker into, if any. */
export function useColumnsSlot() {
  return useContext(TableSlotsContext)?.columns ?? null
}

/** The slot a `DataTable` should render its edit toggle into, if any. */
export function useTableActionsSlot() {
  return useContext(TableSlotsContext)?.actions ?? null
}
